import React, { useState, useEffect } from "react";
import { useNavigate, Navigate } from "react-router-dom";
import { useAuth, formatApiErrorDetail } from "../context/AuthContext";
import api from "../lib/api";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Boxes, ArrowRight } from "lucide-react";
import { toast } from "sonner";

const BG = "https://images.unsplash.com/photo-1738844153732-a485f0e78382?crop=entropy&cs=srgb&fm=jpg&q=85&w=1600";

export default function Login() {
  const { user, login, register, checked } = useAuth();
  const navigate = useNavigate();
  const [mode, setMode] = useState("login");
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [form, setForm] = useState({ name: "", email: "", password: "", organization_name: "" });
  const [demo, setDemo] = useState(null);

  useEffect(() => {
    api.get("/config").then(({ data }) => { if (data.demo_available) setDemo(data); }).catch(() => {});
  }, []);

  if (checked && user) return <Navigate to="/" replace />;

  const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

  const submit = async (e) => {
    e.preventDefault();
    setError("");
    setLoading(true);
    try {
      if (mode === "login") {
        await login(form.email, form.password);
      } else {
        await register(form);
      }
      toast.success("Welcome to StockLedger");
      navigate("/");
    } catch (err) {
      setError(formatApiErrorDetail(err.response?.data?.detail) || err.message);
    } finally {
      setLoading(false);
    }
  };

  const isLogin = mode === "login";
  let submitLabel = "Create account";
  if (loading) submitLabel = "Please wait…";
  else if (isLogin) submitLabel = "Sign in";

  const handleGoogle = () => {
    // REMINDER: DO NOT HARDCODE THE URL, OR ADD ANY FALLBACKS OR REDIRECT URLS, THIS BREAKS THE AUTH
    const redirectUrl = window.location.origin + "/";
    window.location.href = `https://auth.emergentagent.com/?redirect=${encodeURIComponent(redirectUrl)}`;
  };

  return (
    <div className="min-h-screen grid lg:grid-cols-2 bg-background">
      {/* Left visual */}
      <div className="hidden lg:block relative border-r border-border">
        <img src={BG} alt="warehouse" className="absolute inset-0 w-full h-full object-cover" />
        <div className="absolute inset-0 bg-black/60" />
        <div className="relative z-10 h-full flex flex-col justify-between p-12 text-white">
          <div className="flex items-center gap-2">
            <div className="w-9 h-9 bg-primary flex items-center justify-center">
              <Boxes className="w-6 h-6 text-primary-foreground" />
            </div>
            <span className="font-display font-black text-xl tracking-tighter">StockLedger</span>
          </div>
          <div>
            <h2 className="font-display text-5xl font-black tracking-tighter leading-none">
              Raw to<br />finished.<br />Fully tracked.
            </h2>
            <p className="mt-4 text-white/70 max-w-sm text-sm">
              Inventory, Bill-of-Materials production, sales, purchasing & accounting —
              multi-warehouse and multi-currency (₦ default).
            </p>
          </div>
        </div>
      </div>

      {/* Right form */}
      <div className="flex items-center justify-center p-6 sm:p-12">
        <div className="w-full max-w-md">
          <div className="lg:hidden flex items-center gap-2 mb-8">
            <div className="w-8 h-8 bg-primary flex items-center justify-center">
              <Boxes className="w-5 h-5 text-primary-foreground" />
            </div>
            <span className="font-display font-black text-lg tracking-tighter">StockLedger</span>
          </div>

          <div className="text-xs uppercase tracking-[0.2em] text-muted-foreground font-semibold mb-2">
            {mode === "login" ? "Sign in" : "Create organization"}
          </div>
          <h1 className="font-display text-4xl font-black tracking-tighter mb-8">
            {mode === "login" ? "Welcome back." : "Start fresh."}
          </h1>

          <form onSubmit={submit} className="space-y-4" data-testid="auth-form">
            {mode === "register" && (
              <>
                <div>
                  <Label className="text-xs uppercase tracking-wider">Your name</Label>
                  <Input className="rounded-none mt-1" value={form.name} onChange={set("name")}
                    required data-testid="reg-name" placeholder="Ada Lovelace" />
                </div>
                <div>
                  <Label className="text-xs uppercase tracking-wider">Organization name</Label>
                  <Input className="rounded-none mt-1" value={form.organization_name} onChange={set("organization_name")}
                    required data-testid="reg-org" placeholder="Acme Manufacturing" />
                </div>
              </>
            )}
            <div>
              <Label className="text-xs uppercase tracking-wider">Email</Label>
              <Input type="email" className="rounded-none mt-1" value={form.email} onChange={set("email")}
                required data-testid="auth-email" placeholder="you@company.com" />
            </div>
            <div>
              <Label className="text-xs uppercase tracking-wider">Password</Label>
              <Input type="password" className="rounded-none mt-1" value={form.password} onChange={set("password")}
                required data-testid="auth-password" placeholder="••••••••" />
            </div>

            {error && (
              <div className="border border-destructive bg-destructive/10 text-destructive text-sm px-3 py-2" data-testid="auth-error">
                {error}
              </div>
            )}

            <Button type="submit" disabled={loading}
              className="w-full rounded-none h-11 gap-2 font-semibold transition-transform duration-200 hover:-translate-y-0.5"
              data-testid="auth-submit">
              {submitLabel}
              <ArrowRight className="w-4 h-4" />
            </Button>
          </form>

          <div className="flex items-center gap-3 my-5">
            <div className="h-px flex-1 bg-border" />
            <span className="text-xs uppercase tracking-widest text-muted-foreground">or</span>
            <div className="h-px flex-1 bg-border" />
          </div>

          <button
            type="button"
            onClick={handleGoogle}
            data-testid="google-login-btn"
            className="w-full h-11 border border-border bg-card flex items-center justify-center gap-3 text-sm font-semibold transition-colors duration-200 hover:bg-secondary"
          >
            <img src="https://www.gstatic.com/firebasejs/ui/2.0.0/images/auth/google.svg" alt="" className="w-5 h-5" />
            Continue with Google
          </button>

          <div className="mt-6 text-sm text-muted-foreground">
            {mode === "login" ? "New here?" : "Already have an account?"}{" "}
            <button
              className="text-foreground font-semibold underline underline-offset-4"
              onClick={() => { setMode(mode === "login" ? "register" : "login"); setError(""); }}
              data-testid="auth-toggle-mode">
              {mode === "login" ? "Create an organization" : "Sign in"}
            </button>
          </div>

          {mode === "login" && demo && (
            <div className="mt-6 border border-border bg-card p-3 text-xs font-mono text-muted-foreground" data-testid="demo-hint">
              Demo admin — {demo.demo_email} / {demo.demo_password}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
